// Gesture physics from Apple's "Designing Fluid Interfaces", translated for the web.

/** Distance a flick travels before it stops, given release velocity in px/s (scroll-like deceleration). */
export function project(velocity: number, decelerationRate = 0.998): number {
  return ((velocity / 1000) * decelerationRate) / (1 - decelerationRate)
}

/** Soft boundary: the further past the edge, the less the element follows. */
export function rubberband(overshoot: number, dimension: number, constant = 0.55): number {
  return (overshoot * dimension * constant) / (dimension + constant * Math.abs(overshoot))
}

type Sample = { t: number; x: number; y: number }

/** Keeps the last few pointer positions so release velocity reflects the final motion, not the whole drag. */
export class VelocityTracker {
  private samples: Sample[] = []
  add(t: number, x: number, y: number) {
    this.samples.push({ t, x, y })
    if (this.samples.length > 8) this.samples.shift()
  }
  reset(t: number, x: number, y: number) {
    this.samples = [{ t, x, y }]
  }
  /** px/s over the last ~100ms; zero if the pointer paused before release. */
  velocity(now: number): { vx: number; vy: number } {
    const recent = this.samples.filter((s) => now - s.t <= 100)
    if (recent.length < 2) return { vx: 0, vy: 0 }
    const a = recent[0]
    const b = recent[recent.length - 1]
    const dt = (b.t - a.t) / 1000
    if (dt <= 0) return { vx: 0, vy: 0 }
    return { vx: (b.x - a.x) / dt, vy: (b.y - a.y) / dt }
  }
}

/** Elements that keep their own pointer behavior inside a draggable surface. */
export const INTERACTIVE = 'button, a, input, textarea, select, label, [role="switch"], [data-no-drag]'
